import type { JSX } from 'react';
import type { SurfaceOf } from './types';
import { SvgChart } from './SvgChart';
import { flagClass, formatDate } from './format';

type Chart = SurfaceOf<'chart'>;

/** Fixed categorical order (validated palette); never cycled past four series. */
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];

interface Row {
  x: string;
  label: string;
  [series: string]: string | number | undefined;
}

export function ChartSurfaceView({ surface }: { surface: Chart }): JSX.Element {
  const xs = Array.from(new Set(surface.series.flatMap((s) => s.points.map((p) => p.x)))).sort();
  const rows: Row[] = xs.map((x) => {
    const row: Row = { x, label: formatDate(x) };
    for (const s of surface.series) {
      const p = s.points.find((pt) => pt.x === x);
      if (p) {
        row[s.name] = p.y;
        if (p.flag) row[`${s.name}__flag`] = p.flag;
      }
    }
    return row;
  });
  const range = surface.referenceRange;
  const hasData = rows.length > 0;
  const svgSeries = surface.series.map((s, i) => ({
    name: s.name,
    color: SERIES_COLORS[i % SERIES_COLORS.length] ?? '#2a78d6',
    points: s.points.map((p) => ({ x: p.x, label: formatDate(p.x), y: p.y, ...(p.flag ? { flag: p.flag } : {}) })),
  }));
  return (
    <section className="surface surface-chart">
      <h3 className="surface-title">
        {surface.title}
        {surface.unit ? <span className="muted small"> ({surface.unit})</span> : null}
      </h3>
      {!hasData ? (
        <p className="muted">None recorded</p>
      ) : (
        <SvgChart kind={surface.kind} series={svgSeries} title={surface.title} {...(surface.unit ? { unit: surface.unit } : {})} {...(range ? { referenceRange: range } : {})} />
      )}
      {range && (range.low !== undefined || range.high !== undefined) ? (
        <p className="muted small">
          Reference range: {range.low ?? '…'} – {range.high ?? '…'} {surface.unit ?? ''}
        </p>
      ) : null}
      {surface.caption ? <p className="caption">{surface.caption}</p> : null}
      <details className="chart-table">
        <summary className="small">Data table</summary>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                {surface.series.map((s) => (
                  <th key={s.name}>{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.x}>
                  <td>{r.label}</td>
                  {surface.series.map((s) => (
                    <td key={s.name}>
                      {r[s.name] ?? '—'} {typeof r[`${s.name}__flag`] === 'string' ? <span className={flagClass(String(r[`${s.name}__flag`]))}>{String(r[`${s.name}__flag`])}</span> : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
